import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { auth, db } from '../firebaseConfig'; // Adjust path if needed
import { doc, getDoc, setDoc } from 'firebase/firestore';
import '../components/MyProfile.css'; // Import the new CSS file
import { Link } from 'react-router-dom';

const MyProfile = () => {
  const [userData, setUserData] = useState({
    companyName: '',
    companyAddress: '',
    keyPersonName: '',
    keyPersonEmail: '',
    phoneNumber: '',
    marketingConsent: false,
    consentTimestamp: null
  });

  const [originalData, setOriginalData] = useState(null); // Stores the original state before editing
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const currentUser = auth.currentUser;

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const docRef = doc(db, 'users', currentUser.phoneNumber);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const fetchedData = docSnap.data();
            setUserData({
              companyName: fetchedData.companyName || '',
              companyAddress: fetchedData.companyAddress || '',
              keyPersonName: fetchedData.keyPersonName || '',
              keyPersonEmail: fetchedData.keyPersonEmail || '',
              phoneNumber: fetchedData.phoneNumber || currentUser.phoneNumber || '',
              marketingConsent: !!fetchedData.marketingConsent,
              consentTimestamp: fetchedData.consentTimestamp || null,
            });
            setOriginalData(fetchedData); // Save the original data
          } else {
            console.error('No user data found');
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      setLoading(false);
    };

    fetchUserData();

    // Silently check if current user is an admin — no error shown if not
    if (currentUser) {
      currentUser.getIdToken().then((token) =>
        fetch('/api/admin/verify', { headers: { Authorization: `Bearer ${token}` } })
      ).then((res) => { if (res.ok) setIsAdmin(true); }).catch(() => {});
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserData({ ...userData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSave = async () => {
    if (currentUser) {
      try {
        const docRef = doc(db, 'users', currentUser.phoneNumber);
        const payload = { ...userData };
        // If user newly opted in, stamp the time (keeps prior timestamp if already opted)
        if (userData.marketingConsent && !originalData?.marketingConsent) {
        payload.consentTimestamp = new Date().toISOString();
        }
        await setDoc(docRef, payload, { merge: true });
        alert('Profile updated successfully!');
        setOriginalData(payload); // Update originalData after saving
        setEditing(false); // Exit editing mode
      } catch (error) {
        console.error('Error updating user data:', error);
      }
    }
  };

  const handleCancel = () => {
    setUserData(originalData); // Restore the original data
    setEditing(false); // Exit editing mode
  };

  if (loading) {
    return (
      <Layout>
        <p>Loading...</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="profile-page">
        <h1>My Profile</h1>
        {isAdmin && (
          <Link to="/admin" style={{ display: 'inline-block', marginBottom: 16, padding: '8px 18px', background: '#0f172a', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
            Admin Panel
          </Link>
        )}
        {editing ? (
          <div>
            <h2>Company Details</h2>
            <label>
              Company Name:
              <input
                type="text"
                name="companyName"
                value={userData.companyName}
                onChange={handleChange}
              />
            </label>
            <label>
              Company Address:
              <input
                type="text"
                name="companyAddress"
                value={userData.companyAddress}
                onChange={handleChange}
              />
            </label>

            <h2>Key Person Details</h2>
            <label>
              Name:
              <input
                type="text"
                name="keyPersonName"
                value={userData.keyPersonName}
                onChange={handleChange}
              />
            </label>
            <label>
              Email:
              <input
                type="email"
                name="keyPersonEmail"
                value={userData.keyPersonEmail}
                onChange={handleChange}
              />
            </label>

            <h2>Phone Number</h2>
            <p>{currentUser.phoneNumber}</p>

            {/* -------- Consent block (optional, does not block anything) -------- */}
            <div className="consent-box">
              <label className="consent-label">
                <input
                  type="checkbox"
                  name="marketingConsent"
                  checked={!!userData.marketingConsent}
                  onChange={handleChange}
                />
                <span>
                  I agree to receive product updates, newsletters, and marketing emails from
                  Tender Managers. I can unsubscribe anytime. Read our{' '}
                  <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                </span>
              </label>
              {userData.marketingConsent && (
                <p className="consent-note">
                  Opted in{userData.consentTimestamp ? ` on ${new Date(userData.consentTimestamp).toLocaleString()}` : ''}.
                </p>
              )}
            </div>
            {/* ------------------------------------------------------------------ */}
            <button onClick={handleSave}>Save Changes</button>
            <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
          </div>
        ) : (
          <div>
            <h2>Company Details</h2>
            <p><strong>Name:</strong> {userData.companyName || 'N/A'}</p>
            <p><strong>Address:</strong> {userData.companyAddress || 'N/A'}</p>

            <h2>Key Person Details</h2>
            <p><strong>Name:</strong> {userData.keyPersonName || 'N/A'}</p>
            <p><strong>Email:</strong> {userData.keyPersonEmail || 'N/A'}</p>

            <h2>Phone Number</h2>
            <p>{currentUser.phoneNumber}</p>

            <h2>Email Updates</h2>
            <p>
              {userData.marketingConsent
                ? `Opted in${userData.consentTimestamp ? ` on ${new Date(userData.consentTimestamp).toLocaleDateString()}` : ''}.`
                : 'Not opted in.'}
            </p>
            <button onClick={() => setEditing(true)}>Edit Profile</button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyProfile;
